import { addToTopWithDeduplication } from 'utils/array';

import type SearchPrediction from 'data/search-predictions';
import type { Filter, LocationFilter, PropertyType } from 'contexts/preferences/listing-params/types';

//#region Types
/**
 * The non-location filter dimensions active at the moment a search was
 * performed. Deliberately excludes `LocationFilter`'s fields (slug,
 * latitude, longitude, zoom, areaName, boundary) — those describe *where*
 * the filter was captured, not *what* it was, and restoring them wholesale
 * would clobber the new location's own values set right after navigation.
 */
export type FilterSnapshot = Omit<Filter, keyof LocationFilter>;

export interface DetailedRecentSearchEntry {
  /** Stable dedup/render key — see buildDetailedRecentSearchKey. */
  key: string;
  /** Same plain, JSON-safe shape already used by the existing recent-searches list. */
  location: SearchPrediction;
  filterSnapshot: FilterSnapshot;
  searchedAt: string;
}
//#endregion

const PROPERTY_TYPES_IN_FIXED_ORDER: PropertyType[] = ['house', 'townhouse', 'condo', 'land', 'commercial', 'farm'];

/** Strips the location fields out of a live `Filter`, leaving only what should be restored/summarized. */
export function buildFilterSnapshot(filter: Filter): FilterSnapshot {
  const { slug, longitude, latitude, zoom, areaName, boundary, ...filterSnapshot } = filter;
  return filterSnapshot;
}

/**
 * A stable fingerprint of the *meaningful* filter dimensions, built from
 * named fields in a fixed order (not `JSON.stringify`, whose key order for
 * a dynamically-built object isn't a contract worth relying on). Two
 * snapshots that differ in any of these fields get different keys, so the
 * same city with a different filter is intentionally a distinct entry.
 */
export function buildDetailedRecentSearchKey(description: string, filterSnapshot: FilterSnapshot): string {
  const homeTypeFingerprint = PROPERTY_TYPES_IN_FIXED_ORDER.map(type => `${type}:${!!filterSnapshot.homeType?.[type]}`).join(',');
  const fields = [
    description.toLowerCase(),
    `rental:${filterSnapshot.rental}`,
    `status:${filterSnapshot.status}`,
    `bedrooms:${filterSnapshot.bedrooms}`,
    `bathrooms:${filterSnapshot.bathrooms}`,
    `parkingSpaces:${filterSnapshot.parkingSpaces}`,
    `priceMin:${filterSnapshot.priceMin}`,
    `priceMax:${filterSnapshot.priceMax}`,
    `sqftMin:${filterSnapshot.sqftMin}`,
    `sqftMax:${filterSnapshot.sqftMax}`,
    `homeType:${homeTypeFingerprint}`,
    `openHouse:${filterSnapshot.openHouse}`,
    `garage:${filterSnapshot.garage}`,
    `pool:${filterSnapshot.pool}`,
    `fireplace:${filterSnapshot.fireplace}`,
    `waterfront:${filterSnapshot.waterfront}`,
    `hasImage:${filterSnapshot.hasImage}`,
    `showComingSoon:${filterSnapshot.showComingSoon}`,
    `listedSince:${filterSnapshot.listedSince}`,
    `listedTo:${filterSnapshot.listedTo}`,
    `locker:${filterSnapshot.additional?.condoOrTownhouse?.locker}`,
    `maintenanceFee:${filterSnapshot.additional?.condoOrTownhouse?.maintenanceFee}`,
    `providerId:${filterSnapshot.providerId}`,
  ];
  return fields.join('|');
}

/**
 * Builds a SearchPrediction-shaped object for whatever location `filter`
 * currently points at — used when a filter changes on an already-loaded
 * area/hybrid page, where there's no fresh SearchPrediction from a search
 * click, only the active `listingParams.filter`. Consumers (rendering,
 * restoring on click) already tolerate this plain-object shape: it's the
 * same one localStorage rehydration produces once class methods are lost.
 */
export function buildLocationFromFilter(filter: Pick<Filter, 'slug' | 'areaName' | 'latitude' | 'longitude'>): SearchPrediction {
  return {
    id: `location-${filter.slug}`,
    description: filter.areaName,
    group: 'locations',
    label: null,
    matchedSubstrings: [],
    data: { position: { coordinates: [filter.longitude, filter.latitude], type: 'Point' } },
    source: null,
    identifier: null,
  } as unknown as SearchPrediction;
}

/**
 * Computes the updated list after a filter change on the location the user
 * is currently viewing (as opposed to a brand-new location search, which
 * goes through the plain add-on-search path in PreferencesContext).
 *
 * If the most recent entry is for this same location, it's replaced in
 * place — refining filters while staying on one page continues that same
 * browsing session rather than stacking a new row per tweak. Otherwise
 * (a different location, or no entry yet — e.g. arriving via a bookmark)
 * this opens a new session the normal way, deduplicated like any search.
 */
export function upsertCurrentLocationFilter(
  current: DetailedRecentSearchEntry[] | undefined,
  location: SearchPrediction,
  filter: Filter,
  searchedAt: string
): DetailedRecentSearchEntry[] {
  const filterSnapshot = buildFilterSnapshot(filter);
  const newEntry: DetailedRecentSearchEntry = {
    key: buildDetailedRecentSearchKey(location.description, filterSnapshot),
    location,
    filterSnapshot,
    searchedAt,
  };

  const [mostRecent, ...rest] = current ?? [];
  const isSameBrowsingSession = !!mostRecent
    && mostRecent.location.description.toLowerCase() === location.description.toLowerCase();

  return isSameBrowsingSession
    ? [newEntry, ...rest]
    : addToTopWithDeduplication(current, newEntry, entry => entry.key);
}
